<template>
    <section class="section section-shaped section-lg my-0">
        <div class="shape shape-style-1 bg-gradient-default">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
        </div>
        <div class="container pt-lg-md">
            <div class="row justify-content-center">
              <!-- Content Start -->
              <div class="col-lg-8 col-md-10 mx-auto">
                <h1 class="post-heading">On the importance of a Lead VC
</h1>	

                <span class="meta"> Posted in January 2023 </span>

                <figure>
                  <img class="img-fluid" src="img/blog/card/break-from-the-sheep.jpg" alt="">
                </figure>
                
<p>For startup founders raising venture funding, securing a commitment from a Lead VC is a critical milestone, arguably the most important, in the fundraising process itself. The Lead VC makes a commitment, they go out on a limb. This reassures other investors to co-invest and thus enables a timely closing. This proves particularly necessary at the earliest stages of a startup, when uncertainty is highest, and when the founder's vision for their venture appears at its craziest.</p>
<p>In my experience with early-stage venture investing over a variety of geographies (first the U.S., then Europe, now Japan), Japan undoubtedly strikes me as the most demonstrative example of the relevance of this principle. The pool of VC funds in Japan willing to invest at the Pre-seed, Seed, and even Series A stages of a startup is far too inadequate relative to the supply of talented entrepreneurs with ambitious projects in this large market. </p>
<p>Moreover, for the limited group of VC funds who are willing to invest at the early stages, scarcely any will volunteer to lead the deal. "Come back once you've found a Lead VC," is an all-too-common refrain, if it's not the old standby of, "Come back when you're ready for the Series A..."</p>
<p>What is a Lead VC ?</p>
<p>In nascent startup ecosystems, sometimes the interpretation of the very definition of a Lead VC can be unclear. Here's how I define it.</p>
<p>The Lead VC in a fundraising round is the VC fund who makes the first firm commitment to invest in the startup. They express their commitment in writing, and they specify the terms and valuation of their proposed investment. Once agreement with the founders is reached, the Lead VC structures the fundraising round and establishes the schedule for signing and closing. They also generally perform the bulk of the due diligence effort for the investment transaction. The Lead VC does not necessarily represent the largest check in an investment round, but they inevitably serve to catalyze the fundraising process with the commitment and structure they bring.</p>
<p>Why the trepidation ?</p>
<p>It is very tempting for prospective investors in a startup to take a wait-and-see approach. Investors love to collect data to comfort their internal decision-making process. VC operators have a fiduciary duty to their own fund’s investors, so it is their obligation to perform sufficient due diligence on any prospective investment.</p>
<p>Making decisions with incomplete information is inherently uncomfortable. This is especially true in a broader historical environment which was unforgiving of mistakes and stigmatized failures. Becoming comfortable to embrace uncertainty, especially when it involves investing other people’s money, takes time. It is perfectly understandable that new VCs — and CVCs for that matter — often defer investment decisions into early-stage companies until substantial validation comes in. </p>
<p>This produces a pair of diverging goals. The founder aspires to close their fundraising round as quickly as possible so that they can return to their core mission of building the business. The investor, on the other hand, prefers to wait for further validation. Phrases like, "Come back for a future round," or "Come back once you've found a Lead VC," are classic symptoms of an investor waiting for further validation.</p>
<p>However, our belief is that venture investing is impossible to perform successfully without a conviction to pull the trigger on a deal in the context of incomplete information. Innovation involves uncertainty. Early-stage companies do not have all the answers. </p>
<p>Having said this, there is nothing inherently wrong with VC funds whose strategy is to follow, not lead. Some funds, such as CVCs who invest for corporate synergies, prefer to let independent financial VC funds set the terms and valuation. Other funds simply feel more comfortable acting in a follower role, and have established such an expectation with their fund LPs. Finally, for reasons of limited bandwidth at a given time, some VC funds will lead some deals while serving as a follower in others. Serving as the Lead VC in a transaction requires far more effort than merely joining a syndicate as a follower.</p>
<p>Transparency is the key</p>
<p>Because the Lead / Follower disposition of a VC investor is amorphous, not to mention that it is often further obfuscated with misleading marketing statements, I would submit that one of the best questions a founder can ask an investor in their initial discussions is something like the following:</p>
<p>"Does your fund generally serve as Lead VC on new investments, or rather as follower ?"</p>
<p>or</p>
<p>"Should our conversations about a prospective investment from your fund progress favorably, would you hypothetically serve in the role of Lead VC or prefer to follow ?"</p>



              </div>
            </div>
        </div>
    </section>
</template>
<script>
export default {};
</script>
<style>
.post-heading,
.subheading,
.section-heading,
.blockquote,
.meta,
p,
span {
  color: #fff;
}
.post-heading {
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
}
.subheading {
  font-size: 20px;
  margin-top: 16px;
}
.section-heading {
  font-weight: 700;
  margin-top: 32px;
}
.section-heading + p {
  margin-top: 16px;
}
.meta + p {
  margin-top: 32px;
}
.author-info {
  font-size: 14px;
  font-style: italic;
  line-height: 1.5;
}
figure {
  margin-top: 24px;
}
</style>

